import { useMemo } from 'react';
import type { PlayerPosition, ProximityConfig, PeerAudioState } from '../types';
import { calculateDistance, calculateVolume, DEFAULT_PROXIMITY_CONFIG } from '../utils/distance';

interface UseProximityAudioOptions {
  myPeerId: string;
  positions: Record<string, PlayerPosition>;
  config?: ProximityConfig;
}

interface UseProximityAudioReturn {
  peerAudioStates: PeerAudioState[];
  myPosition: PlayerPosition | null;
}

/**
 * Hook to calculate proximity-based audio volumes for all peers
 */
export function useProximityAudio({
  myPeerId,
  positions,
  config = DEFAULT_PROXIMITY_CONFIG,
}: UseProximityAudioOptions): UseProximityAudioReturn {
  
  const result = useMemo(() => {
    const myPosition = positions[myPeerId] || null;
    
    // DEBUG: Check for peer ID mismatch if we have positions but can't find ourselves
    if (!myPosition && Object.keys(positions).length > 0) {
      console.warn('[ProxChat] Proximity Warning: Could not find my own position!', {
        myPeerId,
        availablePeerIds: Object.keys(positions),
        matchFound: Object.keys(positions).includes(myPeerId)
      });
    }

    const selfName = myPosition?.name;
    
    if (!myPosition) {
      // If we don't yet have a position for self, we cannot compute distances.
      // Critically: do NOT show "self" as a nearby player (it can happen if the
      // server temporarily keys self under a different id).
      const peerAudioStates: PeerAudioState[] = Object.entries(positions)
        .filter(([peerId, pos]) => peerId !== myPeerId && (!selfName || pos.name !== selfName))
        .map(([peerId, pos]) => ({
          peerId,
          name: pos.name,
          volume: 1.0,
          distance: 0,
          muted: pos.muted,
          connected: pos.connected,
          isSelf: false,
        }));
      
      return { peerAudioStates, myPosition };
    }

    // Calculate volume for each peer based on distance
    const peerAudioStates: PeerAudioState[] = Object.entries(positions)
      .filter(([peerId, peerPos]) => peerId !== myPeerId && (!selfName || peerPos.name !== selfName))
      .map(([peerId, peerPos]) => {
        const distance = calculateDistance(myPosition, peerPos);
        const volume = peerPos.muted ? 0 : calculateVolume(distance, config);
        
        return {
          peerId,
          name: peerPos.name,
          volume,
          distance,
          muted: peerPos.muted,
          connected: peerPos.connected,
          isSelf: false,
        };
      })
      // Sort by distance for UI display
      .sort((a, b) => a.distance - b.distance);

    return { peerAudioStates, myPosition };
  }, [myPeerId, positions, config]);

  return result;
}
